/**
 * Hydrates a "Shop the Look" dialog with a product's PDP top section.
 *
 * One element, two usages:
 *   - Per-card (look cards): one `.ts-look-card__cta` button; the product
 *     URL comes from `data-product-url` on the element.
 *   - Section-shared (PDP "Shop the Look"): the element wraps the whole
 *     layout; the "View all" button (`.ts-look-card__cta` — or a host-owned
 *     CTA matched by `data-cta-selector` — with its own `data-product-url`)
 *     opens one shared dialog.
 *
 * Inside the dialog, the look's products are presented via a selector rail
 * (`[data-product-selector]`, one thumbnail per product, rendered by
 * `ts-shop-the-look-modal.liquid`) and prev/next chevrons
 * (`[data-product-nav]`) — both swap which product the dialog shows. The
 * injected PDP's multi-image gallery is CSS-hidden and replaced by a single
 * image built from its first media.
 *
 * Content per product URL is fetched once (through the TS PDP template, via
 * `data-pdp-view`) and cached, so switching back to a product is instant.
 *
 * Click flow: show a loading overlay, fetch the product page, inject its
 * `.product-information` into the (already-open) dialog, hide the overlay.
 */

import { watchThirdPartyModals } from '@ts/modal-overlay-guard';
import { initKiwiSizeGuide, isKiwiHydrated } from '@ts/kiwi-hydration';

/** @typedef {HTMLElement & { showDialog?: () => void }} DialogComponentElement */

class TsLookCardModalTrigger extends HTMLElement {
  /** @type {boolean} */
  #inited = false;
  /** @type {HTMLElement | null} */
  #content = null;
  /** @type {HTMLElement | null} */
  #dialog = null;
  /** @type {DialogComponentElement | null} */
  #dialogComponent = null;
  /** @type {HTMLElement | null} */
  #loadingOverlay = null;
  /**
   * Pristine `.product-information` snapshot per product URL. Never inserted
   * itself — showing a product that isn't already mounted injects a fresh
   * clone (see #open).
   * @type {Map<string, Element>}
   */
  #sections = new Map();
  /**
   * @type {Map<string, string>} Product path → rail group, learned from swatch
   * swaps — a selected colorway may not be in the look's product list, so the
   * rail alone can't map it back to its group.
   */
  #groupOverrides = new Map();
  /** @type {HTMLElement[]} Dialog-owned product prev/next chevrons. */
  #navButtons = [];
  /** @type {string} */
  #currentUrl = '';
  /** @type {string} Product URL of the section currently mounted in the dialog. */
  #injectedUrl = '';
  /** @type {boolean} */
  #loading = false;
  /** @type {boolean} Whether the last trigger activation was pointer-driven. */
  #pointerOpen = false;

  connectedCallback() {
    if (this.#inited) return;
    this.#inited = true;

    // Anchor every lookup on the look-card dialog's own class: product cards
    // rendered inside this trigger carry their own quick-view modal, whose
    // dialog-component / [data-modal-content] / [data-loading-overlay] come
    // FIRST in DOM order — a loose querySelector would grab those instead.
    this.#dialog = this.querySelector('.ts-look-card__dialog');
    this.#content = this.#dialog?.querySelector('[data-modal-content]') ?? null;
    this.#dialogComponent = /** @type {DialogComponentElement | null} */ (
      this.#dialog?.closest('dialog-component') ?? null
    );
    this.#loadingOverlay = this.querySelector('.ts-look-card__loading-overlay');

    if (!this.#content || !this.#dialogComponent) return;

    // Relocate the dialog to <main id="MainContent">. The trigger lives inside
    // the "How we wear it" <slideshow-component>; if the dialog stays there,
    // the injected product gallery's own <slideshow-component> finds an
    // ancestor slideshow and self-disables (overflow:hidden, no swipe), plus
    // it hits containing-block / flex-percentage weirdness from the outer
    // carousel. Target <main>, not <body>: <main> carries
    // `data-template="product.ts-pdp-standard"` that almost every TS style is
    // scoped under, and it escapes the slideshow nesting.
    const main = document.querySelector('main#MainContent') || document.body;
    main.append(this.#dialogComponent);

    // TS PDP styles are scoped under [data-template='product.ts-pdp-standard'],
    // which <main> only carries on actual PDPs. Stamp the scope on the dialog
    // wrapper so the injected content styles on any host page (homepage etc.).
    if (this.dataset.pdpView) {
      this.#dialogComponent.dataset.template = `product.${this.dataset.pdpView}`;
    }

    // The parent slideshow preventDefaults mousedown to suppress synthetic
    // clicks during drag; stop it from swallowing our trigger clicks.
    /** @param {Event} event */
    const stop = (event) => event.stopPropagation();
    const body = this.closest('.ts-look-card__body');
    if (body) body.addEventListener('mousedown', stop);
    if (this.#dialog) this.#dialog.addEventListener('mousedown', stop);

    // Delegated, in the CAPTURE phase: the card's `<product-card>` binds its
    // own click handler (view-transition navigation) closer to the target, so
    // a bubble listener here fires too late to stop it. Capturing lets us
    // preventDefault + stopPropagation before both that handler and the native
    // link navigation run.
    this.addEventListener('click', (event) => this.#onClick(event), true);

    if (this.#dialog) {
      // Hold direct references: the chevrons get relocated into each injected
      // product's media wrapper, and a variant-change morph can drop them from
      // the DOM — a dialog query would then never find them again.
      this.#navButtons = /** @type {HTMLElement[]} */ (Array.from(this.#dialog.querySelectorAll('[data-product-nav]')));

      // When a variant is selected inside the modal, <media-gallery> re-renders,
      // orphaning the single-image view built from it. Re-apply on the next tick.
      // The morph can also wipe the Kiwi size-guide render — re-init only then:
      // the product hasn't changed, so a surviving rendered anchor is still
      // valid, and skipping avoids a redundant Kiwi pass + link flash per click.
      this.#dialog.addEventListener('variant:update', () => {
        requestAnimationFrame(() => {
          if (this.#content) {
            this.#applyModalTweaks(this.#content, this.#currentUrl);
            if (!isKiwiHydrated(this.#content)) initKiwiSizeGuide(this.#content);
            this.#initTrueFit(this.#content);
          }
        });
      });

      // The selector rail and product-nav chevrons live inside the dialog,
      // which was just relocated out of this element — bind on the dialog.
      this.#dialog.addEventListener('click', (event) => this.#onDialogClick(event));

      // Colorway swatches in the injected PDP are links to sibling
      // color-product PDPs (see ts-color-swatches) — swap the modal content
      // to that colorway in place instead of letting the page navigate.
      this.#dialog.addEventListener('click', this.#onSwatchClick);

      // Live modality tracking while open: keydown = keyboard (restore the
      // close button's focus ring that a pointer-driven open suppresses — see
      // #openDialog), pointerdown = pointer (re-arms the close backstop below
      // in mixed sessions). #openDialog deliberately leaves the attribute
      // alone on already-open product switches — these listeners own it.
      this.#dialog.addEventListener('keydown', () => {
        this.#dialog?.removeAttribute('data-pointer-open');
      });
      this.#dialog.addEventListener('pointerdown', () => {
        this.#dialog?.setAttribute('data-pointer-open', 'true');
      });

      // Backstop for TS-477: the pre-open blur in #openDialog should leave
      // dialog.close() nothing to restore focus to, but if focus was somehow
      // reacquired, trim the restored-focus ring here (a one-frame flash is
      // possible on this path — the close event fires after restoration
      // paints). Pointer-only sessions only; keyboard sessions (attribute
      // already cleared) keep the ring.
      this.#dialog.addEventListener('close', () => {
        if (!this.#dialog?.hasAttribute('data-pointer-open')) return;
        this.#dialog.removeAttribute('data-pointer-open');
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body) active.blur();
      });
    }
  }

  /** @param {Event} event */
  #onDialogClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || typeof target.closest !== 'function') return;

    const item = target.closest('[data-product-selector] [data-product-url]');
    if (item instanceof HTMLElement) {
      const url = item.dataset.productUrl;
      // No-op when the clicked group is already on display — the displayed
      // product may be a sibling colorway of the rail item's product.
      const currentGroup = this.#groupOf(this.#currentUrl);
      const sameGroup = Boolean(currentGroup) && item.dataset.productGroup === currentGroup;
      if (url && !sameGroup && productPath(url) !== productPath(this.#currentUrl)) this.#open(url);
      return;
    }

    const nav = target.closest('[data-product-nav]');
    if (nav instanceof HTMLElement) {
      this.#openAdjacent(nav.dataset.productNav === 'next' ? 1 : -1);
    }
  }

  /**
   * All rail buttons — including the hidden sibling-colorway entries that only
   * exist to map a colorway URL back to its product group.
   * @returns {HTMLElement[]}
   */
  #selectorItems() {
    if (!this.#dialog) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(this.#dialog.querySelectorAll('[data-product-selector] [data-product-url]'))
    );
  }

  /**
   * The visible, clickable rail buttons — one per product group.
   * @returns {HTMLElement[]}
   */
  #visibleSelectorItems() {
    return this.#selectorItems().filter((item) => !item.hidden);
  }

  /**
   * Resolve the product group of `url` via the rail (hidden siblings included).
   * @param {string} url
   * @returns {string | null}
   */
  #groupOf(url) {
    const path = productPath(url);
    const match = this.#selectorItems().find((item) => productPath(item.dataset.productUrl || '') === path);
    return match?.dataset.productGroup || this.#groupOverrides.get(path) || null;
  }

  /**
   * Open the previous/next product in the selector rail, wrapping around.
   * @param {number} delta
   */
  #openAdjacent(delta) {
    const items = this.#visibleSelectorItems();
    if (items.length < 2) return;
    const currentGroup = this.#groupOf(this.#currentUrl);
    const currentPath = productPath(this.#currentUrl);
    let index = items.findIndex((item) => {
      if (currentGroup) return item.dataset.productGroup === currentGroup;
      return productPath(item.dataset.productUrl || '') === currentPath;
    });
    if (index === -1) index = delta > 0 ? -1 : 0;
    const url = items[(index + delta + items.length) % items.length]?.dataset.productUrl;
    if (url) this.#open(url);
  }

  /**
   * Mark the rail item whose product group matches `url` as current.
   * @param {string} url
   */
  #updateSelector(url) {
    const group = this.#groupOf(url);
    const currentPath = productPath(url);
    for (const item of this.#visibleSelectorItems()) {
      const selected = group
        ? item.dataset.productGroup === group
        : productPath(item.dataset.productUrl || '') === currentPath;
      item.setAttribute('aria-current', selected ? 'true' : 'false');
    }
  }

  /**
   * Reflect a colorway swap on its group's rail item: retarget the item to the
   * displayed URL (so reopening it — by click or prev/next — returns to the
   * selected color) and swap its thumbnail to the displayed product's first
   * media.
   * @param {string} url
   * @param {Element} section - The injected `.product-information`.
   */
  #updateSelectorPreview(url, section) {
    const group = this.#groupOf(url);
    if (!group) return;
    const item = this.#visibleSelectorItems().find((entry) => entry.dataset.productGroup === group);
    if (!item || productPath(item.dataset.productUrl || '') === productPath(url)) return;

    item.dataset.productUrl = url;

    const image = section.querySelector('media-gallery .product-media-container img');
    const thumb = item.querySelector('.ts-look-card__selector-image');
    if (image instanceof HTMLImageElement && thumb instanceof HTMLImageElement && image.src) {
      thumb.src = imageRendition(image.src, 176);
      // The Liquid-rendered srcset/sizes still describe the original image.
      thumb.removeAttribute('srcset');
      thumb.removeAttribute('sizes');
    }

    const title = section.querySelector('h1')?.textContent?.trim();
    if (title) item.setAttribute('aria-label', title);
  }

  /**
   * Resolve which product URL (if any) a click targets.
   * @param {Event} event
   * @returns {{ url: string, isLink: boolean } | null}
   */
  #resolveTrigger(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || typeof target.closest !== 'function') return null;

    // Button trigger (e.g. the look card's "Shop the Look", or "View all").
    // data-cta-selector lets a host match its own CTA markup (e.g. the promo
    // sections' section-owned ts-cta anchors) instead of .ts-look-card__cta.
    const cta = target.closest(this.dataset.ctaSelector || '.ts-look-card__cta');
    if (cta && this.contains(cta)) {
      const url = /** @type {HTMLElement} */ (cta).dataset.productUrl || this.dataset.productUrl || '';
      // Anchor CTAs carry a real href as the no-JS fallback; flag them so the
      // click handler prevents the navigation when the modal opens.
      const isLink = cta instanceof HTMLAnchorElement && cta.hasAttribute('href');
      return { url, isLink };
    }

    return null;
  }

  /** @param {Event} event */
  #onClick(event) {
    const trigger = this.#resolveTrigger(event);
    if (!trigger || !trigger.url) return;
    // Card links would otherwise navigate to the PDP.
    if (trigger.isLink) event.preventDefault();
    event.stopPropagation();
    // Keyboard-activated clicks carry an empty pointerType (and are plain
    // MouseEvents in older engines) — only real taps/clicks count as pointer.
    this.#pointerOpen = event instanceof PointerEvent && event.pointerType !== '';
    this.#open(trigger.url);
  }

  /**
   * Intercept colorway-swatch link clicks inside the dialog and load that
   * color-product into the modal instead of navigating to its PDP.
   * @param {Event} event
   */
  #onSwatchClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || typeof target.closest !== 'function') return;

    const swatch = target.closest('.ts-color-swatches a[href]');
    if (!swatch) return;

    event.preventDefault();
    event.stopPropagation();

    const href = swatch.getAttribute('href');
    // Re-selecting the current colorway is a no-op (still no navigation).
    if (!href || swatch.getAttribute('aria-current') === 'true') return;

    // The sibling shares the displayed product's rail group; remember the
    // mapping so the rail highlight/preview can track colorways the look's
    // product list doesn't include.
    const group = this.#groupOf(this.#currentUrl);
    if (group) this.#groupOverrides.set(productPath(href), group);

    this.#open(href);
  };

  /** @param {string} url */
  async #open(url) {
    const cached = this.#sections.get(url);
    // Claim #currentUrl only for a request that will actually display —
    // claiming it before the loading guard would make the same-group check in
    // #onDialogClick swallow every later click on this product's rail item.
    if (!cached && this.#loading) return;
    this.#currentUrl = url;

    // A shown section is never re-inserted. Removing and re-adding it would
    // reconnect its custom elements, and Horizon's product-form (media,
    // price-per-item, sticky ATC too) registers its listeners with a one-shot
    // AbortController that disconnect aborts — the reconnected form would
    // never hear another variant update and keep submitting the last variant
    // id it saw. Re-opening on the mounted product leaves it untouched; any
    // other cached product gets a fresh clone of its snapshot.
    if (cached) {
      this.#openDialog();
      if (this.#injectedUrl === url && this.#content?.firstElementChild) return;
      await new Promise(requestAnimationFrame);
      this.#inject(/** @type {Element} */ (cached.cloneNode(true)), url);
      return;
    }

    this.#loading = true;
    this.#showOverlay();

    try {
      // The TS PDP composition is the default product template, so the plain
      // product URL already renders it — no `?view=` needed. (`data-pdp-view`
      // still drives the dialog's data-template stamp in connectedCallback.)
      const fetchUrl = new URL(url, window.location.origin);
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.product-information');
      if (!section) {
        console.error('[ts-look-card] could not locate .product-information in response');
        return;
      }
      if (!this.#content) return;

      // Shopify subsets the compiled CSS/JS bundles per page, so on non-PDP
      // hosts the injected markup's styles/components are missing — adopt the
      // fetched page's assets before revealing the content.
      await this.#adoptAssets(doc);

      // Snapshot before the tweaks: they move the dialog-owned chevrons into
      // the section, and a clone taken afterwards would duplicate them.
      this.#sections.set(url, /** @type {Element} */ (section.cloneNode(true)));

      // Open BEFORE injecting so the gallery slideshow measures the real
      // viewport instead of a 0×0 `display: none` dialog.
      this.#openDialog();
      await new Promise(requestAnimationFrame);
      this.#inject(section, url);
    } catch (error) {
      console.error('[ts-look-card] failed to load product:', error);
    } finally {
      this.#hideOverlay();
      this.#loading = false;
    }
  }

  /**
   * Inject a `.product-information` element into the open dialog, sync the
   * selector rail to it, and hydrate the third-party widgets it carries.
   * @param {Element} section
   * @param {string} url
   */
  #inject(section, url) {
    if (!this.#content) return;
    this.#applyModalTweaks(section, url);
    this.#content.replaceChildren(section);
    this.#injectedUrl = url;
    this.#applyColorScheme(section);
    this.#updateSelector(url);
    this.#updateSelectorPreview(url, section);
    initKiwiSizeGuide(this.#content);
    this.#initTrueFit(this.#content);
  }

  /**
   * Adopt the fetched page's stylesheet links and module scripts that this
   * page doesn't already have, and wait (briefly) for the new styles to load
   * so the modal doesn't flash unstyled content.
   * @param {Document} doc
   */
  async #adoptAssets(doc) {
    const existingLinks = new Set(
      Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(
        (el) => /** @type {HTMLLinkElement} */ (el).href
      )
    );
    /** @type {Promise<void>[]} */
    const pending = [];

    for (const el of Array.from(doc.querySelectorAll('link[rel="stylesheet"]'))) {
      const href = /** @type {HTMLLinkElement} */ (el).href;
      if (!href || existingLinks.has(href)) continue;
      existingLinks.add(href);

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      pending.push(
        new Promise((resolve) => {
          link.onload = link.onerror = () => resolve(undefined);
        })
      );
      document.head.append(link);
    }

    const existingScripts = new Set(
      Array.from(document.querySelectorAll('script[src]')).map((el) => /** @type {HTMLScriptElement} */ (el).src)
    );

    for (const el of Array.from(doc.querySelectorAll('script[src][type="module"]'))) {
      const src = /** @type {HTMLScriptElement} */ (el).src;
      if (!src || existingScripts.has(src)) continue;
      existingScripts.add(src);

      const script = document.createElement('script');
      script.type = 'module';
      script.src = src;
      document.head.append(script);
    }

    // Don't block the modal forever on a slow stylesheet.
    const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
    await Promise.race([Promise.all(pending), timeout]);
  }

  /**
   * Copy the section's color-scheme class onto the dialog so its chrome uses
   * the same scheme as the hosted product. Swap on each product change.
   * @param {Element} section
   */
  #applyColorScheme(section) {
    if (!this.#dialog) return;
    for (const c of Array.from(this.#dialog.classList)) {
      if (c.startsWith('color-')) this.#dialog.classList.remove(c);
    }
    const colorSchemeClass = Array.from(section.classList).find((c) => c.startsWith('color-'));
    if (colorSchemeClass) this.#dialog.classList.add(colorSchemeClass);
  }

  /**
   * Replace the injected multi-image gallery with a single image + product-nav
   * chevrons, and append "View full details".
   * @param {ParentNode} root
   * @param {string} url
   */
  #applyModalTweaks(root, url) {
    this.#buildMediaView(root);

    const productDetails = root.querySelector('.product-details');
    const detailsHost = productDetails?.querySelector(':scope > .group-block') ?? productDetails;
    if (detailsHost && !detailsHost.querySelector('.ts-look-card__view-details')) {
      const link = document.createElement('a');
      link.className = 'ts-cta ts-cta--medium ts-look-card__view-details';
      link.href = url;
      link.textContent = this.dataset.viewDetailsLabel || '';
      detailsHost.append(link);
    }
  }

  /**
   * Build the single-image media view inside the injected product's
   * `.ts-pdp-media-wrapper` (its <media-gallery> is CSS-hidden in the modal)
   * and relocate the dialog-owned product-nav chevrons into it. Rebuilt on
   * every (re)inject so variant changes refresh the image.
   * @param {ParentNode} root
   */
  #buildMediaView(root) {
    const wrapper = root.querySelector('.ts-pdp-media-wrapper');
    if (!wrapper) return;

    wrapper.querySelector('.ts-look-card__media-view')?.remove();
    const view = document.createElement('div');
    view.className = 'ts-look-card__media-view';
    const firstImage = wrapper.querySelector('media-gallery .product-media-container img');
    if (firstImage) {
      const image = /** @type {HTMLImageElement} */ (firstImage.cloneNode(true));
      image.removeAttribute('loading');
      view.append(image);
    }
    wrapper.append(view);

    // The chevrons are single elements owned by the dialog — appending them
    // here moves them into the current product's wrapper, whose
    // `position: relative` anchors their overlay placement.
    for (const nav of this.#navButtons) {
      nav.hidden = false;
      wrapper.append(nav);
    }
  }

  /**
   * Hydrate the injected True Fit "What's My Size" placeholder
   * (`.tfc-fitrec-product`, server-rendered by `blocks/ts-true-fit.liquid`).
   * True Fit's library only scans for placeholders once at page load, so a
   * placeholder injected after that stays empty until we request a re-scan
   * via `tfcapi('calculate')`. The stub queues calls until the library loads,
   * so no readiness wait is needed. An already-hydrated widget (variant
   * updates morph around it) is left alone.
   * @param {HTMLElement} content
   */
  #initTrueFit(content) {
    // The fit modal the widget opens is a <body>-level overlay that a
    // showModal() dialog would otherwise paint over — install the top-layer
    // guard before any re-scan.
    watchThirdPartyModals();
    const placeholder = content.querySelector('.tfc-fitrec-product');
    if (!placeholder || placeholder.childElementCount > 0) return;
    const tfcapi = /** @type {any} */ (window).tfcapi;
    if (typeof tfcapi !== 'function') return;
    try {
      tfcapi('calculate');
    } catch (error) {
      console.error('[ts-look-card] True Fit re-scan failed:', error);
    }
  }

  #showOverlay() {
    if (this.#loadingOverlay) this.#loadingOverlay.hidden = false;
  }

  #hideOverlay() {
    if (this.#loadingOverlay) this.#loadingOverlay.hidden = true;
  }

  #openDialog() {
    const alreadyOpen = this.#dialog?.hasAttribute('open');
    // Blur the tap-focused element BEFORE opening: dialog.close() restores
    // focus to whatever held it at showModal() time — on an iOS tap that's
    // the HWWI slideshow ancestor of the CTA — and WebKit paints the restored
    // focus :focus-visible, ringing the section (TS-477). Reacting on `close`
    // is too late (the ring flashes for a frame before a blur can land);
    // with <body> as the pre-open focus there is nothing to restore or ring.
    if (this.#pointerOpen && !alreadyOpen) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body) active.blur();
    }
    this.#dialogComponent?.showDialog?.();
    // showModal() focuses the close button; iOS Safari flags that programmatic
    // focus as :focus-visible even after a tap (taps never focus buttons in
    // WebKit, so the focus-visible state carries over), drawing an unwanted
    // ring around the X (TS-475). Stamp pointer-driven opens so the modal CSS
    // can suppress it — but only on the closed→open transition: showDialog()
    // no-ops when already open, so product switches re-enter here with
    // #pointerOpen still holding the ORIGINAL trigger's modality, and
    // re-stamping it would suppress a keyboard user's focus ring (WCAG 2.4.7).
    // While open, the dialog's keydown/pointerdown listeners own the attribute.
    if (!alreadyOpen) this.#dialog?.toggleAttribute('data-pointer-open', this.#pointerOpen);
  }
}

/**
 * Normalize a product URL to its pathname so variants of the same product URL
 * (with/without origin, query params) compare equal.
 * @param {string} url
 * @returns {string}
 */
const productPath = (url) => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
};

/**
 * Point a Shopify CDN image URL at a small rendition via the `width` query
 * param (the form `image_url` emits), so the rail thumbnail doesn't load the
 * gallery-size image.
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
const imageRendition = (src, width) => {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
};

if (!customElements.get('ts-look-card-modal-trigger')) {
  customElements.define('ts-look-card-modal-trigger', TsLookCardModalTrigger);
}
